import type { ChartV2Series, ChartV2SeriesType } from '../types/chart-v2.types';
import {
  formatPrice,
  formatTooltipTime,
  formatYAxisTick,
  yAxisTickDecimals,
} from '../utils/format';

import { isOhlcPoint, pointValue } from '../utils/helpers';
import { getYAxisTicks, yAxisTickCount } from './nice-ticks';
import type { Scales, SeriesScale, Viewport } from './scales';
import { generateXAxisTicks } from './x-ticks';

export interface DrawTheme {
  background: string;
  /** Default text color for headers / labels (e.g. y-axis unit header). */
  foreground: string;
  gridLine: string;
  axisLine: string;
  axisLabel: string;
  crosshair: string;
  /**
   * Soft surface used for the x-axis crosshair pill. Slightly darker
   * than `background` so the label reads against the chart canvas
   * without needing a border (matches v1's `surface.default` look).
   */
  hoverPillBackground: string;
  seriesDefault: string;
  tooltipBackground: string;
  tooltipForeground: string;
  up: string;
  down: string;
  fontDefault: string;
  fontMono: string;
}

export interface DrawContext {
  ctx: CanvasRenderingContext2D;
  scales: Scales;
  theme: DrawTheme;
  viewport: Viewport;
  showXAxis: boolean;
  /**
   * Width of each y-axis column. Falls back to `Y_AXIS_COLUMN_WIDTH`
   * when the caller doesn't size it dynamically. Sized to fit the widest
   * tick label so long values (e.g. `1,234,567.89`) aren't clipped.
   */
  columnWidth?: number;
}

/** Minimum / default y-axis column width when labels are short. */
export const Y_AXIS_COLUMN_WIDTH = 56;
/**
 * Slack added around the widest measured tick label: tick text is centered
 * in its column and the latest-price / hover pills are left-aligned with a
 * small inset, so reserve a little room on both sides.
 */
const Y_AXIS_LABEL_PADDING = 12;

/** Minimum horizontal gap (px) kept between adjacent x-axis labels. */
const X_AXIS_LABEL_GAP = 8;

let labelMeasureCtx: CanvasRenderingContext2D | null = null;

/**
 * Compute the y-axis column width needed to fit `labels` without clipping,
 * floored at `Y_AXIS_COLUMN_WIDTH`. Measured with the same mono font the
 * axis ticks render in. Returns the floor if measurement isn't available
 * (e.g. no DOM / 2d context).
 */
export const measureYAxisColumnWidth = (
  font: string,
  labels: string[]
): number => {
  if (labels.length === 0 || typeof document === 'undefined') {
    return Y_AXIS_COLUMN_WIDTH;
  }

  if (!labelMeasureCtx) {
    labelMeasureCtx = document.createElement('canvas').getContext('2d');
  }

  if (!labelMeasureCtx) {
    return Y_AXIS_COLUMN_WIDTH;
  }

  labelMeasureCtx.font = font;

  let widest = 0;

  for (const label of labels) {
    const w = labelMeasureCtx.measureText(label).width;

    if (w > widest) {
      widest = w;
    }
  }

  return Math.max(
    Y_AXIS_COLUMN_WIDTH,
    Math.ceil(widest) + Y_AXIS_LABEL_PADDING
  );
};

/** Resolve the per-column y-axis width, defaulting when unsized. */
const resolveColumnWidth = (dc: DrawContext): number =>
  dc.columnWidth ?? Y_AXIS_COLUMN_WIDTH;

/**
 * Left edge (x) of series column `i`: the y-axis line, an 8px gutter, then
 * one `columnWidth` per preceding column.
 */
const seriesColumnX = (
  scales: Scales,
  i: number,
  columnWidth: number
): number => scales.plot.x + scales.plot.width + 8 + i * columnWidth;

export const clearCanvas = (
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme
): void => {
  ctx.save();
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.restore();
};

export const drawGrid = (dc: DrawContext): void => {
  const { ctx, scales, theme, viewport, showXAxis } = dc;
  const { plot, xDomain } = scales;
  const primary = scales.perSeries[0];
  const bottomY = showXAxis ? plot.y + plot.height : viewport.height;

  ctx.save();
  ctx.strokeStyle = theme.gridLine;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);

  if (primary) {
    const yTicks = getYAxisTicks(
      [primary.yDomain[0], primary.yDomain[1]],
      yAxisTickCount(plot.height)
    );

    for (const t of yTicks) {
      const y = Math.round(primary.yScale(t)) + 0.5;

      ctx.beginPath();
      ctx.moveTo(plot.x, y);
      ctx.lineTo(plot.x + plot.width, y);
      ctx.stroke();
    }
  }

  const xTicks = generateXAxisTicks(xDomain, scales.barTimeline);

  for (const tick of xTicks) {
    const x = Math.round(scales.xScale(tick.ms)) + 0.5;

    if (x < plot.x - 0.5 || x > plot.x + plot.width + 0.5) {
      continue;
    }

    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, bottomY);
    ctx.stroke();
  }

  ctx.restore();
};

const LABEL_PILL_HEIGHT = 22;
const LABEL_PILL_PADDING_X = 5;
const LABEL_PILL_RADIUS = 3;

/**
 * Draw a rounded rectangle. Uses native `ctx.roundRect` where available
 * (all modern browsers), falls back to manually stroked path.
 */
const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  ctx.beginPath();

  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
    return;
  }

  const radius = Math.min(r, w / 2, h / 2);

  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawYAxisLabelPill = (
  ctx: CanvasRenderingContext2D,
  theme: DrawTheme,
  label: string,
  centerX: number,
  bottomY: number
) => {
  if (!label) {
    return;
  }

  ctx.save();
  ctx.font = theme.fontDefault;

  const textWidth = ctx.measureText(label).width;
  const pillWidth = Math.ceil(textWidth) + LABEL_PILL_PADDING_X * 2;
  // Center the pill horizontally over its column so it matches the
  // original `AxisUnit` badge (which uses `justify-content: center`
  // inside a fixed-width column).
  const pillX = Math.round(centerX - pillWidth / 2) + 0.5;
  const pillY = Math.round(bottomY - LABEL_PILL_HEIGHT) + 0.5;

  ctx.fillStyle = theme.background;
  ctx.strokeStyle = theme.axisLine;
  ctx.lineWidth = 1;
  drawRoundedRect(
    ctx,
    pillX,
    pillY,
    pillWidth,
    LABEL_PILL_HEIGHT,
    LABEL_PILL_RADIUS
  );
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = theme.foreground;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    label,
    pillX + LABEL_PILL_PADDING_X,
    pillY + LABEL_PILL_HEIGHT / 2
  );
  ctx.restore();
};

const drawOneYAxisColumn = (
  dc: DrawContext,
  opts: {
    seriesScale: SeriesScale;
    seriesLabel: string;
    /**
     * Horizontal centre of the column. Tick labels and the header
     * pill are both centered here so each label has equal slack on
     * its left and right inside the column — important when multiple
     * y-axes stack and the inner column would otherwise read
     * lopsided (right-aligned ticks crowd the separator while leaving
     * a wide gap toward the y-axis line).
     */
    labelCenterX: number;
  }
) => {
  const { seriesScale, seriesLabel, labelCenterX } = opts;
  const { ctx, scales, theme } = dc;
  const { plot } = scales;
  const ticks = getYAxisTicks(
    [seriesScale.yDomain[0], seriesScale.yDomain[1]],
    yAxisTickCount(plot.height)
  );

  // Column header pill (e.g. "USD", "%", or an indicator short-name).
  // Neutral outline + background mirrors the original `AxisUnit` Badge.
  drawYAxisLabelPill(ctx, theme, seriesLabel, labelCenterX, plot.y - 4);

  ctx.save();
  ctx.font = theme.fontMono;
  ctx.fillStyle = theme.axisLabel;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const decimals = yAxisTickDecimals(ticks);

  for (const t of ticks) {
    const y = seriesScale.yScale(t);

    ctx.fillText(formatYAxisTick(t, decimals), labelCenterX, y);
  }

  ctx.restore();
};

export interface DrawYAxesOptions {
  /**
   * When true, draw a single y-axis column shared across all series
   * (used by the `indexed` price-display mode). Otherwise each series gets
   * its own column.
   */
  merged?: boolean;
  /** Label used for the shared column when `merged` is true. */
  mergedLabel?: string;
}

export const drawYAxes = (
  dc: DrawContext,
  seriesLabels: string[],
  options: DrawYAxesOptions = {}
): void => {
  const { ctx, scales, theme, viewport, showXAxis } = dc;
  const { plot } = scales;
  const columnWidth = resolveColumnWidth(dc);
  const bottomY = showXAxis ? plot.y + plot.height : viewport.height;

  ctx.save();
  ctx.strokeStyle = theme.axisLine;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.beginPath();
  const axisX = Math.round(plot.x + plot.width) + 0.5;

  ctx.moveTo(axisX, 0);
  ctx.lineTo(axisX, bottomY);
  ctx.stroke();
  ctx.restore();

  if (options.merged) {
    const primary = scales.perSeries[0];

    if (!primary) {
      return;
    }

    // Merged column runs from the y-axis line (`plot.x + plot.width`) to
    // the right edge of the single column; center the pill within it.
    const mergedColumnLeft = scales.plot.x + scales.plot.width;
    const mergedColumnRight = mergedColumnLeft + columnWidth;

    drawOneYAxisColumn(dc, {
      seriesScale: { ...primary, color: theme.axisLabel },
      labelCenterX: (mergedColumnLeft + mergedColumnRight) / 2,
      seriesLabel: options.mergedLabel ?? '',
    });

    return;
  }

  scales.perSeries.forEach((sc, i) => {
    const label = seriesLabels[i] ?? sc.seriesId;
    const baseColumnX = seriesColumnX(scales, i, columnWidth);

    if (i > 0) {
      ctx.save();
      ctx.strokeStyle = theme.axisLine;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      const separatorX = Math.round(baseColumnX - 4) + 0.5;

      ctx.moveTo(separatorX, 0);
      ctx.lineTo(separatorX, bottomY);
      ctx.stroke();
      ctx.restore();
    }

    // Column bounds: the first column starts at the y-axis line; every
    // subsequent column starts 4px after its separator. All columns are
    // `Y_AXIS_COLUMN_WIDTH` wide. Center the pill in that bounding box.
    const columnLeft =
      i === 0 ? scales.plot.x + scales.plot.width : baseColumnX - 4;
    const columnRight = columnLeft + columnWidth;

    drawOneYAxisColumn(dc, {
      seriesScale: sc,
      labelCenterX: (columnLeft + columnRight) / 2,
      seriesLabel: label,
    });
  });
};

const drawXAxisLabels = (dc: DrawContext) => {
  const { ctx, scales, theme, viewport } = dc;
  const { plot, xDomain } = scales;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = theme.fontDefault;

  // Measure every visible tick up-front so colliding labels can be dropped.
  const visible = generateXAxisTicks(xDomain, scales.barTimeline)
    .map(tick => {
      const anchorX = scales.xScale(tick.ms);
      const halfWidth = ctx.measureText(tick.label).width / 2;
      // Keep the full label inside the canvas — edge ticks are centered on
      // the tick mark by default, which clips the first/last character when
      // the mark sits on the plot boundary (e.g. "Jun 28" → "un 28").
      const x = Math.max(
        halfWidth,
        Math.min(viewport.width - halfWidth, anchorX)
      );

      return { tick, anchorX, x, left: x - halfWidth, right: x + halfWidth };
    })
    .filter(
      t => t.anchorX >= plot.x - 0.5 && t.anchorX <= plot.x + plot.width + 0.5
    );

  const placed: Array<{ left: number; right: number }> = [];
  const fits = (left: number, right: number): boolean =>
    placed.every(
      p =>
        right + X_AXIS_LABEL_GAP <= p.left || left - X_AXIS_LABEL_GAP >= p.right
    );

  const y = plot.y + plot.height + 6;

  // Two passes so a dense run of minor ticks can't crowd out the highlights
  // (day / month / year boundaries): highlights claim their space first, then
  // minor ticks fill whatever gaps remain.
  for (const highlightPass of [true, false]) {
    for (const t of visible) {
      if (!!t.tick.highlight !== highlightPass || !fits(t.left, t.right)) {
        continue;
      }

      placed.push({ left: t.left, right: t.right });
      ctx.fillStyle = t.tick.highlight ? theme.foreground : theme.axisLabel;
      ctx.fillText(t.tick.label, t.x, y);
    }
  }
};

export const drawXAxis = (dc: DrawContext): void => {
  const { ctx, scales, theme } = dc;
  const { plot } = scales;

  ctx.save();
  ctx.strokeStyle = theme.axisLine;

  drawXAxisLabels(dc);

  ctx.beginPath();
  ctx.moveTo(plot.x, plot.y + plot.height + 0.5);
  ctx.lineTo(plot.x + plot.width, plot.y + plot.height + 0.5);
  ctx.stroke();
  ctx.restore();
};

const clipPlot = (dc: DrawContext, fn: () => void) => {
  const { ctx, scales } = dc;
  const { plot } = scales;

  ctx.save();
  ctx.beginPath();
  ctx.rect(plot.x, plot.y, plot.width, plot.height);
  ctx.clip();
  fn();
  ctx.restore();
};

/**
 * Half-open index range `[start, end)` of a time-sorted point array that
 * covers `domain`, plus one point on each side so the entry/exit segments
 * still reach the clip edges. Lets the draw loops skip off-screen points
 * instead of mapping + drawing the whole series every frame: `clipPlot`
 * already hides off-domain segments, but on a zoomed-in view of a long
 * series that's thousands of wasted `xScale` + `lineTo` calls per redraw.
 * Assumes `data` is sorted ascending by `time` (every chartv2 series is).
 */
export const visibleRange = <T extends { time: number }>(
  data: T[],
  domain: [number, number]
): { start: number; end: number } => {
  // First index with time >= domain[0]; step back one to keep the segment
  // entering from the left edge.
  let lo = 0;
  let hi = data.length;

  while (lo < hi) {
    const mid = (lo + hi) >>> 1;

    if (data[mid].time < domain[0]) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const start = Math.max(0, lo - 1);

  // First index with time > domain[1]; keep one more for the exit segment.
  lo = start;
  hi = data.length;

  while (lo < hi) {
    const mid = (lo + hi) >>> 1;

    if (data[mid].time <= domain[1]) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const end = Math.min(data.length, lo + 1);

  return { start, end };
};

type PixelPoint = { x: number; y: number };

const drawLinePath = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
) => {
  const { ctx, scales } = dc;

  if (series.data.length === 0) {
    return;
  }

  const { start, end } = visibleRange(series.data, scales.xDomain);

  // Need at least two points to define a line segment.
  if (end - start < 2) {
    return;
  }

  ctx.strokeStyle = seriesScale.color;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.beginPath();

  const { plot } = scales;
  const rightEdge = plot.x + plot.width;
  const pixelAt = (i: number): PixelPoint => ({
    x: scales.xScale(series.data[i].time),
    y: seriesScale.yScale(pointValue(series.data[i])),
  });

  // `visibleRange` brackets the domain in *time*, but the index x-scale
  // insets bars half a slot from the plot edges, so on fractional pan
  // offsets the buffer point can land up to half a slot *inside* the plot.
  // In that case the segment crossing the edge is the one *beyond* the
  // buffer, so step one more point outward — extrapolating the inner
  // segment instead bends the line in the wrong direction at the edge.
  // Consecutive bars are exactly one slot apart, so one extra point always
  // reaches past the edge. The caller clips to the plot rect, so drawing
  // the off-plot points directly trims to the edge with the true slope —
  // no edge interpolation needed — and when the data itself ends inside
  // the plot the line correctly stops at the first/last real point.
  let first = start;
  let last = end - 1;

  if (first > 0 && pixelAt(first).x > plot.x) {
    first -= 1;
  }

  if (last < series.data.length - 1 && pixelAt(last).x < rightEdge) {
    last += 1;
  }

  let started = false;

  for (let i = first; i <= last; i++) {
    const p = pixelAt(i);

    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) {
      continue;
    }

    if (started) {
      ctx.lineTo(p.x, p.y);
    } else {
      ctx.moveTo(p.x, p.y);
      started = true;
    }
  }

  ctx.stroke();
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Apply an alpha channel to a resolved series colour. Mirrors the legacy
 * chart's hex-alpha math (`Math.round(opacity * 255)`) so the area
 * gradient matches pixel-for-pixel, with an `rgb()` branch as a bonus.
 * Returns `null` for any colour shape we can't safely alpha (e.g.
 * `currentColor`) so the caller can fall back instead of throwing on
 * `addColorStop`.
 */
export const withAlpha = (color: string, opacity: number): string | null => {
  const alpha = clamp01(opacity);
  const hex = /^#([0-9a-f]{6})$/i.exec(color);

  if (hex) {
    const channel = Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0');

    return `#${hex[1]}${channel}`;
  }

  const rgb = /^rgba?\(\s*([^)]+)\)$/i.exec(color);

  if (rgb) {
    const [r, g, b] = rgb[1].split(',').map(part => part.trim());

    if (r && g && b) {
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  return null;
};

const fillAreaFromPath = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
) => {
  const { ctx, scales } = dc;

  if (series.data.length === 0) {
    return;
  }

  const yBase = scales.plot.y + scales.plot.height;
  // Anchor the baseline to the plot's left/right edges, not the first/last
  // data point. With the index x-scale each point sits half a slot inside the
  // plot, so closing the area at the data x leaves its left "wall" floating
  // inside the chart as a stray vertical line. The legacy chart filled
  // edge-to-edge; the fill is clipped to the plot rect by the caller, so
  // edge anchoring is also correct when the data overflows a zoomed window.
  const xRight = scales.plot.x + scales.plot.width;
  const xLeft = scales.plot.x;

  ctx.lineTo(xRight, yBase);
  ctx.lineTo(xLeft, yBase);
  ctx.closePath();

  // Configurable vertical gradient (the simple chart's legacy
  // `lineGradient`): build it from the series colour at each stop's
  // opacity, falling back to the flat fill if any colour can't be alpha'd.
  const stops = series.areaGradient;

  if (stops && stops.length > 0) {
    const stopColors = stops.map(({ opacity }) =>
      withAlpha(seriesScale.color, opacity)
    );

    if (stopColors.every((color): color is string => color !== null)) {
      const gradient = ctx.createLinearGradient(0, scales.plot.y, 0, yBase);

      stops.forEach(({ stop }, i) =>
        gradient.addColorStop(stop, stopColors[i])
      );
      ctx.fillStyle = gradient;
      ctx.fill();

      return;
    }
  }

  ctx.fillStyle = seriesScale.color;
  ctx.globalAlpha = 0.12;
  ctx.fill();
  ctx.globalAlpha = 1;
};

export const drawLineSeries = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
): void => {
  clipPlot(dc, () => {
    drawLinePath(dc, series, seriesScale);

    const shouldFill =
      series.seriesType === 'area' || (series.areaGradient?.length ?? 0) > 0;

    if (shouldFill) {
      fillAreaFromPath(dc, series, seriesScale);
    }
  });
};

// Matches the original chart's `getCandleWidth`: leave ~20% of each slot
// as whitespace between adjacent bars, keep wicks a crisp 1 pixel wide.
const BAR_BODY_RATIO = 0.8;
const MIN_BODY_WIDTH_PX = 3;
const WICK_WIDTH = 1;

/**
 * Derive the on-screen body width from the actual pixel spacing between
 * consecutive bars on the x-scale — taking the MINIMUM spacing so
 * intraday gaps (market close, weekends, holidays) don't inflate the
 * body and cause adjacent candles to overlap.
 *
 * 1m example: bars at 09:30, 09:31, … 16:00, then jumps to 09:30 next
 * day. Average spacing balloons from "1 minute" to "≈20 minutes" once
 * an overnight gap is included — so averaging caused bodies to be 20×
 * wider than they should be. Using the min picks the true 1-minute
 * width and adjacent intra-day bars render with the normal 20% gap.
 */
const computeBarBodyWidth = (
  dc: DrawContext,
  ohlc: Array<{ time: number }>
): number => {
  if (ohlc.length < 2) {
    return 6;
  }

  const sampleSize = Math.min(20, ohlc.length - 1);
  let minSpacing = Number.POSITIVE_INFINITY;

  for (let i = 0; i < sampleSize; i++) {
    const spacing = Math.abs(
      dc.scales.xScale(ohlc[i + 1].time) - dc.scales.xScale(ohlc[i].time)
    );

    if (spacing > 0 && spacing < minSpacing) {
      minSpacing = spacing;
    }
  }

  if (!Number.isFinite(minSpacing)) {
    return 6;
  }

  return Math.max(1, Math.round(minSpacing * BAR_BODY_RATIO));
};

const drawOneCandle = (
  dc: DrawContext,
  opts: {
    seriesScale: SeriesScale;
    p: Extract<ChartV2Series['data'][number], { open: number }>;
    bodyWidth: number;
  }
) => {
  const { seriesScale, p, bodyWidth } = opts;
  const { ctx, scales, theme } = dc;
  const x = Math.round(scales.xScale(p.time));
  const yOpen = seriesScale.yScale(p.open);
  const yClose = seriesScale.yScale(p.close);
  const yHigh = seriesScale.yScale(p.high);
  const yLow = seriesScale.yScale(p.low);
  const isUp = p.close >= p.open;
  const color = isUp ? theme.up : theme.down;

  ctx.fillStyle = color;

  const top = Math.round(Math.min(yOpen, yClose));
  const bottom = Math.round(Math.max(yOpen, yClose));
  const barHigh = Math.round(Math.min(yHigh, yLow));
  const barLow = Math.round(Math.max(yHigh, yLow));
  const bodyLeft = x - Math.floor(bodyWidth / 2);

  if (bodyWidth >= MIN_BODY_WIDTH_PX) {
    // Upper wick (above the body) and lower wick (below the body). Use
    // 1-pixel fillRect columns rather than stroke so the wicks stay
    // crisp at any devicePixelRatio.
    ctx.fillRect(x, barHigh, WICK_WIDTH, Math.max(0, top - barHigh));
    ctx.fillRect(x, bottom + 1, WICK_WIDTH, Math.max(0, barLow - bottom));

    // Body: fill the entire bar width.
    ctx.fillRect(bodyLeft, top, bodyWidth, Math.max(1, bottom - top + 1));
    return;
  }

  // Too crowded for a visible body — fall back to a 1px high-to-low line
  // (same behaviour as the original chart when `barWidth < 3`).
  ctx.fillRect(x, barHigh, WICK_WIDTH, Math.max(1, barLow - barHigh));
};

export const drawCandleSeries = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
): void => {
  const ohlc = series.data.filter(isOhlcPoint);

  if (ohlc.length === 0) {
    return;
  }

  const bodyWidth = computeBarBodyWidth(dc, ohlc);
  const { start, end } = visibleRange(ohlc, dc.scales.xDomain);

  clipPlot(dc, () => {
    for (let i = start; i < end; i++) {
      drawOneCandle(dc, { seriesScale, p: ohlc[i], bodyWidth });
    }
  });
};

const drawOneBar = (
  dc: DrawContext,
  opts: {
    seriesScale: SeriesScale;
    p: Extract<ChartV2Series['data'][number], { open: number }>;
    bodyWidth: number;
  }
) => {
  const { seriesScale, p, bodyWidth } = opts;
  const { ctx, scales, theme } = dc;
  const x = Math.round(scales.xScale(p.time));
  const yOpen = Math.round(seriesScale.yScale(p.open));
  const yClose = Math.round(seriesScale.yScale(p.close));
  const yHigh = Math.round(seriesScale.yScale(p.high));
  const yLow = Math.round(seriesScale.yScale(p.low));
  const isUp = p.close >= p.open;
  const color = isUp ? theme.up : theme.down;

  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  ctx.beginPath();
  ctx.moveTo(x + 0.5, Math.min(yHigh, yLow));
  ctx.lineTo(x + 0.5, Math.max(yHigh, yLow));

  if (bodyWidth >= MIN_BODY_WIDTH_PX) {
    const tick = Math.max(2, Math.floor(bodyWidth / 2));

    ctx.moveTo(x - tick + 0.5, yOpen);
    ctx.lineTo(x + 0.5, yOpen);
    ctx.moveTo(x + 0.5, yClose);
    ctx.lineTo(x + tick + 0.5, yClose);
  }

  ctx.stroke();
};

export const drawBarSeries = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
): void => {
  const ohlc = series.data.filter(isOhlcPoint);

  if (ohlc.length === 0) {
    return;
  }

  const bodyWidth = computeBarBodyWidth(dc, ohlc);
  const { start, end } = visibleRange(ohlc, dc.scales.xDomain);

  clipPlot(dc, () => {
    for (let i = start; i < end; i++) {
      drawOneBar(dc, { seriesScale, p: ohlc[i], bodyWidth });
    }
  });
};

export const drawSeries = (dc: DrawContext, series: ChartV2Series[]): void => {
  series.forEach((s, i) => {
    const scale = dc.scales.perSeries[i];

    if (!scale) {
      return;
    }

    if (s.seriesType === 'candlestick') {
      drawCandleSeries(dc, s, scale);
    } else if (s.seriesType === 'bar') {
      drawBarSeries(dc, s, scale);
    } else {
      drawLineSeries(dc, s, scale);
    }
  });
};

const drawLabelPill = (
  ctx: CanvasRenderingContext2D,
  opts: {
    text: string;
    x: number;
    y: number;
    align: 'left' | 'right' | 'center';
    fill: string;
    color: string;
    paddingY?: number;
  }
) => {
  const { text, x, y, align, fill, color, paddingY = 8 } = opts;
  const paddingX = 8;
  const metrics = ctx.measureText(text);
  const textH =
    (metrics.fontBoundingBoxAscent ?? 0) +
    (metrics.fontBoundingBoxDescent ?? 0);
  const w = Math.ceil(metrics.width) + paddingX * 2;
  const h = textH > 0 ? textH + paddingY * 2 : 18 + (paddingY - 3) * 2;

  let left = x;

  if (align === 'right') {
    left = x - w;
  } else if (align === 'center') {
    left = x - w / 2;
  }

  const top = Math.round(y - h / 2);

  ctx.fillStyle = fill;
  ctx.fillRect(left, top, w, h);

  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(text, left + w / 2, top + h / 2);

  return { left, top, width: w, height: h };
};

/** v1 crosshair dot: series fill + foreground ring on one path. */
const drawCircle = (
  ctx: CanvasRenderingContext2D,
  opts: {
    x: number;
    y: number;
    fill: string;
    stroke: string;
    radius?: number;
  }
): void => {
  const { x, y, fill, stroke, radius = 3 } = opts;

  ctx.setLineDash([]);
  ctx.strokeStyle = stroke;
  ctx.fillStyle = fill;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(Math.round(x), Math.round(y), radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
};

export interface HoverHit {
  seriesId: string;
  color: string;
  value: number;
  seriesType: ChartV2SeriesType;
}

export interface DrawHoverOverlayInput {
  hoverTime: number;
  hits: HoverHit[];
  seriesLabels: string[];
  showTimeLabel?: boolean;
  isIntraday?: boolean;
  /**
   * `'default'` draws the full crosshair (horizontal guide + y-axis price
   * pill per series). `'simple'` mirrors v1's `hoverVariant: 'simple'`
   * used by the simple chart: just the vertical line plus a dot on each
   * line/area series, no horizontal guide, no y-axis pill, no x-axis pill.
   *
   * @default 'default'
   */
  variant?: 'default' | 'simple';
}

export interface DrawLatestPriceInput {
  values: (number | undefined)[];
}

export const drawLatestPriceOverlay = (
  dc: DrawContext,
  input: DrawLatestPriceInput
): void => {
  const { ctx, scales } = dc;
  const { plot } = scales;
  const columnWidth = resolveColumnWidth(dc);

  ctx.save();
  ctx.font = dc.theme.fontMono;

  scales.perSeries.forEach((sc, i) => {
    const value = input.values[i];

    if (value == null || !Number.isFinite(value)) {
      return;
    }

    const y = sc.yScale(value);

    if (!Number.isFinite(y) || y < plot.y || y > plot.y + plot.height) {
      return;
    }

    ctx.strokeStyle = sc.color;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(plot.x, Math.round(y) + 0.5);
    ctx.lineTo(plot.x + plot.width, Math.round(y) + 0.5);
    ctx.stroke();

    ctx.setLineDash([]);

    const pillX = seriesColumnX(scales, i, columnWidth) - 4;

    drawLabelPill(ctx, {
      text: formatPrice(value),
      x: pillX,
      y,
      align: 'left',
      fill: sc.color,
      color: dc.theme.background,
      paddingY: 2,
    });
  });

  ctx.restore();
};

export const drawHoverOverlay = (
  dc: DrawContext,
  input: DrawHoverOverlayInput
): void => {
  const { ctx, scales, theme } = dc;
  const { plot } = scales;
  const columnWidth = resolveColumnWidth(dc);
  const x = scales.xScale(input.hoverTime);

  if (!Number.isFinite(x) || x < plot.x || x > plot.x + plot.width) {
    return;
  }

  ctx.save();

  const isSimple = input.variant === 'simple';

  // v1 simple crosshair: muted vertical guide (--border-hover), [6, 6] dash.
  ctx.strokeStyle = isSimple ? theme.axisLabel : theme.crosshair;
  ctx.lineWidth = 1;
  ctx.setLineDash(isSimple ? [6, 6] : [4, 4]);
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, plot.y);
  ctx.lineTo(Math.round(x) + 0.5, plot.y + plot.height);
  ctx.stroke();

  scales.perSeries.forEach((sc, i) => {
    const hit = input.hits.find(h => h.seriesId === sc.seriesId);

    if (!hit) {
      return;
    }

    const y = sc.yScale(hit.value);

    if (!Number.isFinite(y) || y < plot.y || y > plot.y + plot.height) {
      return;
    }

    if (isSimple) {
      // v1's 'simple' hoverVariant: a dot sitting on the line at the hovered
      // point, no horizontal guide or y-axis price pill. Only line/area
      // series get a marker (candles/bars already read at the crosshair).
      if (hit.seriesType === 'line' || hit.seriesType === 'area') {
        drawCircle(ctx, {
          x,
          y,
          fill: sc.color,
          stroke: theme.foreground,
        });
      }

      return;
    }

    ctx.strokeStyle = sc.color;
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(plot.x, Math.round(y) + 0.5);
    ctx.lineTo(plot.x + plot.width, Math.round(y) + 0.5);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.font = theme.fontMono;

    const pillX = seriesColumnX(scales, i, columnWidth) - 4;

    drawLabelPill(ctx, {
      text: formatPrice(hit.value),
      x: pillX,
      y,
      align: 'left',
      fill: theme.hoverPillBackground,
      color: sc.color,
      paddingY: 2,
    });
  });

  if (!isSimple && input.showTimeLabel !== false) {
    ctx.setLineDash([]);
    ctx.font = theme.fontDefault;

    // Match v1's `drawXAxisMark`: a soft surface pill with no border,
    // sitting just above the x-axis tick row.
    const rawLabel = formatTooltipTime(input.hoverTime);
    const timeLabel =
      input.isIntraday === false
        ? rawLabel.split(' ').slice(0, 3).join(' ')
        : rawLabel;

    drawLabelPill(ctx, {
      text: timeLabel,
      x,
      y: plot.y + plot.height + 12,
      align: 'center',
      fill: theme.hoverPillBackground,
      color: theme.foreground,
    });
  }

  ctx.restore();
};

export const DEFAULT_DRAW_THEME: DrawTheme = {
  background: '#fcfcfc',
  foreground: '#202020',
  gridLine: '#e8e8e8',
  axisLine: '#9aa0a6',
  axisLabel: '#646464',
  crosshair: '#202020',
  hoverPillBackground: '#f0f0f0',
  seriesDefault: '#ffc53d',
  tooltipBackground: '#ffffff',
  tooltipForeground: '#202020',
  up: '#53b365',
  down: '#ec6142',
  fontDefault:
    '12px Nort, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontMono: '12px Roboto Mono, ui-monospace, monospace',
};

const readCssVar = (el: HTMLElement, name: string): string | null => {
  const v = getComputedStyle(el).getPropertyValue(name).trim();

  return v ? v : null;
};

const pickVar = (
  el: HTMLElement,
  names: string[],
  fallback: string
): string => {
  for (const name of names) {
    const v = readCssVar(el, name);

    if (v) {
      return v;
    }
  }

  return fallback;
};

export const resolveThemeFromElement = (el: HTMLElement): DrawTheme => ({
  background: pickVar(
    el,
    ['--chartv2-background', '--background-default', '--surface-default'],
    DEFAULT_DRAW_THEME.background
  ),
  foreground: pickVar(
    el,
    ['--chartv2-foreground', '--text-default'],
    DEFAULT_DRAW_THEME.foreground
  ),
  gridLine: pickVar(
    el,
    ['--chartv2-grid-line', '--border-soft'],
    DEFAULT_DRAW_THEME.gridLine
  ),
  axisLine: pickVar(
    el,
    ['--chartv2-axis-line', '--border-default'],
    DEFAULT_DRAW_THEME.axisLine
  ),
  axisLabel: pickVar(
    el,
    ['--chartv2-axis-label', '--text-soft'],
    DEFAULT_DRAW_THEME.axisLabel
  ),
  crosshair: pickVar(
    el,
    ['--chartv2-crosshair', '--text-default'],
    DEFAULT_DRAW_THEME.crosshair
  ),
  hoverPillBackground: pickVar(
    el,
    ['--chartv2-hover-pill-background', '--surface-default'],
    DEFAULT_DRAW_THEME.hoverPillBackground
  ),
  seriesDefault: pickVar(
    el,
    [
      '--chartv2-series-default',
      '--chart-multi-lines-hex-1',
      '--border-primary',
    ],
    DEFAULT_DRAW_THEME.seriesDefault
  ),
  tooltipBackground: pickVar(
    el,
    ['--chartv2-tooltip-background', '--surface-raised'],
    DEFAULT_DRAW_THEME.tooltipBackground
  ),
  tooltipForeground: pickVar(
    el,
    ['--chartv2-tooltip-foreground', '--text-default'],
    DEFAULT_DRAW_THEME.tooltipForeground
  ),
  up: pickVar(
    el,
    ['--chartv2-up', '--viz-bullish', '--green--5'],
    DEFAULT_DRAW_THEME.up
  ),
  down: pickVar(
    el,
    ['--chartv2-down', '--viz-bearish', '--red--5'],
    DEFAULT_DRAW_THEME.down
  ),
  fontDefault: pickVar(
    el,
    ['--chartv2-font-default'],
    DEFAULT_DRAW_THEME.fontDefault
  ),
  fontMono: pickVar(el, ['--chartv2-font-mono'], DEFAULT_DRAW_THEME.fontMono),
});
